// /////////////////////////////////////////////////////////////
// [ファイル] sfaListColumns.tsx
// [概要] 営業案件一覧（統合版）の列の定義と、表示列の設定（どの列を・どの順で出すか）の保存。
//        列はここに全部定義し、一覧は利用者の設定（キーの並び）に従って必要な列だけを組み立てる。
//        案件名は先頭に固定（外せない・動かせない）。設定は利用者ごとにブラウザ（localStorage）に残し、
//        保存した条件（案件条件リスト）にも含められる。
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import * as Icons from '../../constants/icons';
import { Column } from '../StandardDataTable';
import { BookingChip, OverdueChip, SfaTypeChip, UnitPhaseChip } from './SfaStructureChips';
import { SfaProjectRow, fmtDate, fmtSettlement, fmtYen, fmtYm, formatSystemNumber } from '../../hooks/useSfaStructure';

export interface SfaListColumnDef {
  key: string;
  label: string;
  // 列の組み立て。案件名だけ詳細へ移る navigate を使う
  build: (navigate: (path: string) => void) => Column;
}

// 先頭に固定する列（外せない・動かせない）
export const FIXED_LIST_COLUMN = 'project_name';

const money = (v: number | null | undefined) => (v ? fmtYen(v) : '-');
const redChip = { height: 20, fontSize: '0.7rem', bgcolor: '#ffebee', color: '#c62828' };

// 選べる列の全部（この順が「表示列」タブの既定の並び。既定で出す列は DEFAULT_LIST_COLUMNS）
export const SFA_LIST_COLUMN_DEFS: SfaListColumnDef[] = [
  {
    key: 'project_name', label: '案件名',
    build: (navigate) => ({
      field: 'project_name', headerName: '案件名', width: 320, sticky: true,
      renderCell: (r: SfaProjectRow) => (
        <Box onClick={() => navigate(`/sfa/projects/${r.project_id}`)} sx={{ cursor: 'pointer', '&:hover .name': { textDecoration: 'underline' } }} title="クリックで詳細へ">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>ID: {r.project_id}</Typography>
          <Typography className="name" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'primary.main' }}>{r.project_name}</Typography>
          {r.land_name && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{r.land_name}</Typography>}
        </Box>
      ),
    }),
  },
  {
    key: 'purchase_phase', label: 'フェーズ（仕入）',
    build: () => ({
      field: 'purchase_phase', headerName: 'フェーズ（仕入）', width: 170,
      renderCell: (r: SfaProjectRow) => r.purchase_phase ? <UnitPhaseChip phase={r.purchase_phase} purchase /> : <Typography variant="caption" color="text.disabled">-</Typography>,
    }),
  },
  {
    key: 'rep_phase', label: 'フェーズ（売上）',
    build: () => ({ field: 'rep_phase', headerName: 'フェーズ（売上）', width: 170, renderCell: (r: SfaProjectRow) => <UnitPhaseChip phase={r.rep_phase} /> }),
  },
  {
    key: 'project_kind', label: 'タイプ',
    build: () => ({ field: 'project_kind', headerName: 'タイプ', width: 90, renderCell: (r: SfaProjectRow) => <SfaTypeChip kind={r.project_kind} /> }),
  },
  {
    key: 'booking_ym', label: '決済日 ／ 計上月',
    build: () => ({
      field: 'booking_ym', headerName: '決済日 ／ 計上月', width: 190,
      renderCell: (r: SfaProjectRow) => r.project_kind === 'purchase' ? (
        <Box>
          {r.contract_overdue && <Chip size="small" label="契約予定超過" sx={redChip} />}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{r.settlement_date ? fmtSettlement(r.settlement_date) : r.expected_contract_ym ? `契約予定 ${fmtYm(r.expected_contract_ym)}` : '予定未定'}</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {r.booking_ym ? <BookingChip ym={r.booking_ym} confirmed={r.rep_phase.startsWith('6')} /> : <Typography variant="caption" color="text.secondary">計上月 未定</Typography>}
          <OverdueChip show={r.is_overdue} />
        </Box>
      ),
    }),
  },
  {
    key: 'unit_count', label: '構成（仕入契約／販売区画）',
    build: () => ({
      field: 'unit_count', headerName: '構成（仕入契約／販売区画）', width: 260,
      renderCell: (r: SfaProjectRow) => {
        const flat = r.contract_count === 0 && r.unit_count === 1;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption">
              {flat ? '区画 1 つ（画面に出さない）' : `${r.contract_count ? `仕入 ${r.contract_count} 契約 ／ ` : ''}区画 ${r.unit_count}（受注 ${r.won_count}・見込み ${r.open_count}${r.idle_count ? `・販売前 ${r.idle_count}` : ''}${r.lost_count ? `・失注 ${r.lost_count}` : ''}）`}
            </Typography>
            {r.unassigned_parcel_count > 0 && r.open_count + r.won_count > 0 && (
              <Tooltip title="区画に割り当てていない筆があります"><Chip size="small" label={`未割当 ${r.unassigned_parcel_count} 筆`} sx={redChip} /></Tooltip>
            )}
          </Box>
        );
      },
    }),
  },
  {
    key: 'sales_total', label: '売上高（区画の合計）',
    build: () => ({ field: 'sales_total', headerName: '売上高（区画の合計）', width: 160, align: 'right', renderCell: (r: SfaProjectRow) => money(r.sales_total) }),
  },
  {
    key: 'purchase_total', label: '取得原価（仕入契約の合計）',
    build: () => ({ field: 'purchase_total', headerName: '取得原価（仕入契約の合計）', width: 180, align: 'right', renderCell: (r: SfaProjectRow) => money(r.purchase_total) }),
  },
  { key: 'client_name', label: '取引先（代表）', build: () => ({ field: 'client_name', headerName: '取引先（代表）', width: 160 }) },
  {
    key: 'main_staff_name', label: 'メイン担当',
    build: () => ({
      field: 'main_staff_name', headerName: 'メイン担当', width: 160,
      renderCell: (r: SfaProjectRow) => (
        <Box>
          {r.main_staff_branch_name && <Chip size="small" label={r.main_staff_branch_name} sx={{ height: 18, fontSize: '0.68rem', bgcolor: 'grey.100' }} />}
          <Typography variant="caption" sx={{ display: 'block' }}>{r.main_staff_name || '-'}</Typography>
        </Box>
      ),
    }),
  },
  { key: 'category_large', label: '大項目', build: () => ({ field: 'category_large', headerName: '大項目', width: 150 }) },
  { key: 'category_medium', label: '中項目', build: () => ({ field: 'category_medium', headerName: '中項目', width: 150 }) },
  { key: 'land_name', label: '物件所在地', build: () => ({ field: 'land_name', headerName: '物件所在地', width: 220 }) },
  {
    // 案件番号 (Hozenall NET で採番し工事管理へ引き継ぐ。契約・区画の番号の和集合)
    key: 'system_numbers', label: '案件番号',
    build: () => ({
      field: 'system_numbers', headerName: '案件番号', width: 150,
      renderCell: (r: SfaProjectRow) => r.system_numbers?.length
        ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{r.system_numbers.map((n) => <Chip key={n} size="small" label={formatSystemNumber(n)} sx={{ height: 20, fontSize: '0.72rem' }} />)}</Box>
        : <Typography variant="caption" color="text.disabled">-</Typography>,
    }),
  },
  { key: 'record_type', label: 'レコードタイプ', build: () => ({ field: 'record_type', headerName: 'レコードタイプ', width: 140 }) },
  {
    key: 'created_date', label: '作成日',
    build: () => ({ field: 'created_date', headerName: '作成日', width: 110, renderCell: (r: SfaProjectRow) => fmtDate(r.created_date) }),
  },
  {
    key: 'updated_date', label: '備考 / 更新日',
    build: () => ({
      // 旧「案件リスト」と同じく備考と最終更新日を 1 列にまとめる（見出しクリックは更新日で並べ替え）
      field: 'updated_date', headerName: '備考 / 更新日', width: 250,
      renderCell: (r: SfaProjectRow) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, minWidth: 0 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }} title={r.remarks || undefined}>
            <Icons.AccessTime sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ color: r.remarks ? 'text.primary' : 'text.disabled' }}>{r.remarks || '備考なし'}</Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Icons.CalendarToday sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" noWrap sx={{ color: 'text.disabled' }}>最終更新: {fmtDate(r.updated_date)}</Typography>
          </Box>
        </Box>
      ),
    }),
  },
];

// 「並び替え」タブの選択肢（統合版）。値はバックエンドの sort_cols のキー
export const SFA_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'updated_date', label: '最終更新日' },
  { value: 'created_date', label: '作成日' },
  { value: 'project_name', label: '案件名' },
  { value: 'phase', label: 'フェーズ（判定）' },
  { value: 'purchase_phase', label: 'フェーズ（仕入）' },
  { value: 'rep_phase', label: 'フェーズ（売上）' },
  { value: 'booking_ym', label: '計上月' },
  { value: 'sales_total', label: '売上高（区画の合計）' },
  { value: 'purchase_total', label: '取得原価（仕入契約の合計）' },
  { value: 'client_name', label: '取引先' },
  { value: 'main_staff_name', label: 'メイン担当者' },
];

// 既定の表示列（これまでの一覧と同じ 10 列・同じ順）
export const DEFAULT_LIST_COLUMNS: string[] = [
  'project_name', 'purchase_phase', 'rep_phase', 'project_kind', 'booking_ym', 'unit_count', 'client_name', 'main_staff_name', 'category_large', 'updated_date',
];

// 表示列の選択肢（「表示列」タブと保存した条件の表示に使う）
export const SFA_LIST_COLUMN_OPTIONS = SFA_LIST_COLUMN_DEFS.map((d) => ({ key: d.key, label: d.label }));

const KNOWN = new Set(SFA_LIST_COLUMN_DEFS.map((d) => d.key));

// 設定を正す。知らない列は捨て、重複は 1 つにし、案件名を先頭に置く。空なら既定
export function normalizeListColumns(input: unknown): string[] {
  if (!Array.isArray(input)) return [...DEFAULT_LIST_COLUMNS];
  const seen = new Set<string>([FIXED_LIST_COLUMN]);
  const out = [FIXED_LIST_COLUMN];
  for (const k of input) {
    if (typeof k === 'string' && KNOWN.has(k) && !seen.has(k)) { seen.add(k); out.push(k); }
  }
  return out.length > 1 ? out : [...DEFAULT_LIST_COLUMNS];
}

export const isDefaultListColumns = (cols: string[]) => cols.join(',') === DEFAULT_LIST_COLUMNS.join(',');

// 一覧に渡す列を、設定の順に組み立てる
export function buildListColumns(keys: string[], navigate: (path: string) => void): Column[] {
  const byKey = new Map(SFA_LIST_COLUMN_DEFS.map((d) => [d.key, d]));
  return normalizeListColumns(keys).map((k) => byKey.get(k)!.build(navigate));
}

// 表示列の保存先。利用者ごとにブラウザに残す（タブを閉じても消えない。別の PC では既定に戻る）
export const listColumnsKey = (email: string) => `sfa:projects:columns:v1:${email}`;

export function loadListColumns(email?: string): string[] {
  if (!email) return [...DEFAULT_LIST_COLUMNS];
  try {
    return normalizeListColumns(JSON.parse(localStorage.getItem(listColumnsKey(email)) || 'null'));
  } catch {
    return [...DEFAULT_LIST_COLUMNS];
  }
}

export function saveListColumns(email: string | undefined, cols: string[]): void {
  if (!email) return;
  try {
    localStorage.setItem(listColumnsKey(email), JSON.stringify(cols));
  } catch {
    // 保存できない環境は覚えないだけ
  }
}
